// frontend/src/components/dashboard/Dashboard.js

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import StatsCards from './StatsCards';
import Layout from '../common/Layout';
import ViewLead from '../leads/ViewLead';
import { getUserTimezone, formatInUserTimezone } from '../../utils/timezone';
import '../../styles/Dashboard.css';

function Dashboard() {
  const { user } = useAuth();
  const userTimezone = getUserTimezone();

  const [stats, setStats] = useState(null);
  const [recentActivities, setRecentActivities] = useState([]);
  const [upcomingDemos, setUpcomingDemos] = useState([]);

  const [statsLoading, setStatsLoading] = useState(true);
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const [demosLoading, setDemosLoading] = useState(true);

  const [statsError, setStatsError] = useState(null);
  const [activitiesError, setActivitiesError] = useState(null);
  const [demosError, setDemosError] = useState(null);

  const [viewLeadId, setViewLeadId] = useState(null);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    setStatsError(null);
    try {
      const data = await api.getDashboardStateCounts();
      setStats(data);
    } catch (error) {
      console.error('Dashboard state counts error:', error);
      setStatsError('Failed to load lead statistics');
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const loadRecentActivities = useCallback(async () => {
    setActivitiesLoading(true);
    setActivitiesError(null);
    try {
      const data = await api.getDashboardRecentActivities();
      setRecentActivities(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Recent activities error:', error);
      setActivitiesError('Failed to load recent activities');
    } finally {
      setActivitiesLoading(false);
    }
  }, []);

  const loadUpcomingDemos = useCallback(async () => {
    setDemosLoading(true);
    setDemosError(null);
    try {
      const data = await api.getDashboardUpcomingDemos();
      setUpcomingDemos(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Upcoming demos error:', error);
      setDemosError('Failed to load upcoming demos');
    } finally {
      setDemosLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
    loadRecentActivities();
    loadUpcomingDemos();
  }, [loadStats, loadRecentActivities, loadUpcomingDemos]);

  const refreshDashboard = useCallback(() => {
    loadStats();
    loadRecentActivities();
    loadUpcomingDemos();
  }, [loadStats, loadRecentActivities, loadUpcomingDemos]);

  const handleViewLead = (leadId) => {
    const id = Number(leadId);
    if (!Number.isInteger(id) || id <= 0) return;
    setViewLeadId(id);
  };

  const handleCloseView = () => {
    setViewLeadId(null);
  };

  // ============================================================
  // DATE FORMATTING - UTC to USER TIMEZONE
  // ============================================================

  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      return formatInUserTimezone(date, userTimezone);
    } catch {
      return date;
    }
  };

  // Parse UTC date and convert to user timezone for demo display
  const parseDemoDate = (utcDateString) => {
    let displayDay = '--';
    let displayMonth = '---';
    let displayFull = 'N/A';
    let displayTime = 'Time TBD';
    let dateObj = null;

    if (!utcDateString) {
      return { displayDay, displayMonth, displayFull, displayTime, dateObj };
    }

    try {
      // Parse the UTC date string
      const utcDate = new Date(utcDateString);
      
      // Check if valid
      if (!isNaN(utcDate.getTime())) {
        // Convert to user timezone
        const userDate = new Date(utcDate.toLocaleString('en-US', { timeZone: userTimezone }));
        
        displayDay = userDate.getDate();
        displayMonth = userDate.toLocaleString('default', { month: 'short' });
        displayFull = userDate.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        });
        
        // Format time in user timezone
        displayTime = userDate.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        });
        
        dateObj = userDate;
        
        return { displayDay, displayMonth, displayFull, displayTime, dateObj };
      }

      // Fallback: try to extract from YYYY-MM-DD format
      const dateMatch = String(utcDateString).match(/(\d{4})-(\d{2})-(\d{2})/);
      if (dateMatch) {
        const y = parseInt(dateMatch[1]);
        const m = parseInt(dateMatch[2]) - 1;
        const d = parseInt(dateMatch[3]);
        const utcDate = new Date(Date.UTC(y, m, d));
        const userDate = new Date(utcDate.toLocaleString('en-US', { timeZone: userTimezone }));
        
        displayDay = userDate.getDate();
        displayMonth = userDate.toLocaleString('default', { month: 'short' });
        displayFull = `${displayMonth} ${displayDay}, ${y}`;
        return { displayDay, displayMonth, displayFull, displayTime, dateObj: userDate };
      }
    } catch (e) {
      console.warn('Date parsing error for:', utcDateString, e);
    }

    return { displayDay, displayMonth, displayFull, displayTime, dateObj };
  };

  // Format time in user timezone
  const formatDemoTime = (utcTime, utcDate) => {
    if (!utcTime) return 'Time TBD';
    
    try {
      // If we have a date, combine with time
      if (utcDate) {
        const combined = new Date(utcDate);
        const timeParts = String(utcTime).split(':');
        if (timeParts.length >= 2) {
          combined.setHours(parseInt(timeParts[0]), parseInt(timeParts[1]));
          const userDate = new Date(combined.toLocaleString('en-US', { timeZone: userTimezone }));
          return userDate.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          });
        }
      }
      
      // Fallback: just format the time string
      const parts = String(utcTime).split(':');
      if (parts.length >= 2) {
        return `${parts[0]}:${parts[1]}`;
      }
    } catch (e) {
      console.warn('Time formatting error:', utcTime, e);
    }
    
    return utcTime || 'Time TBD';
  };

  const getActivityIcon = (type) => {
    const icons = {
      'Lead Created': 'plus-circle',
      'Status Changed': 'exchange-alt',
      'Lead Status Changed': 'exchange-alt',
      'Demo Booked': 'calendar-check',
      'Demo Rescheduled': 'calendar-alt',
      'Demo Completed': 'check-circle',
      'Demo Cancelled': 'calendar-times',
      'Phone Call': 'phone',
      'Email': 'envelope',
      'Meeting': 'handshake',
      'Follow-up': 'clock',
      'Note Added': 'sticky-note',
      'Note Updated': 'sticky-note',
      'Documents Uploaded': 'file-upload',
      'Document Deleted': 'file-alt'
    };
    return icons[type] || 'circle';
  };

  const SectionError = ({ message, onRetry }) => (
    <div className="empty-state">
      <i className="fas fa-exclamation-circle"></i>
      <p>{message}</p>
      <button type="button" className="btn btn-secondary btn-sm" onClick={onRetry}>Retry</button>
    </div>
  );

  const SectionSkeleton = ({ rows = 4 }) => (
    <div style={{ padding: '8px 10px' }}>
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} style={{
          height: '36px',
          marginBottom: '6px',
          borderRadius: '6px',
          background: '#f0f3f5',
          animation: 'pulse 1.5s ease-in-out infinite'
        }} />
      ))}
    </div>
  );

  return (
    <Layout>
      {/* Stats Section */}
      <div className="dashboard-stats-section">
        {statsLoading ? (
          <div className="stats-grid">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} style={{ height: '80px', borderRadius: '12px', background: '#f0f3f5' }} />
            ))}
          </div>
        ) : statsError ? (
          <SectionError message={statsError} onRetry={loadStats} />
        ) : (
          <StatsCards stats={stats} />
        )}
      </div>

      {/* Dashboard Grid */}
      <div className="dashboard-grid">

        {/* Recent Activities Card */}
        <div className="dashboard-card">
          <div className="dashboard-card-header">
            <i className="fas fa-clock"></i>
            <span>Recent Activities</span>
            <span className="badge-count">{recentActivities.length}</span>
          </div>
          <div className="dashboard-card-body">
            {activitiesLoading ? (
              <SectionSkeleton rows={5} />
            ) : activitiesError ? (
              <SectionError message={activitiesError} onRetry={loadRecentActivities} />
            ) : recentActivities.length > 0 ? (
              <div className="activity-log">
                {recentActivities.slice(0, 8).map((activity) => {
                  const leadId = activity.lead_id || activity.leadId;
                  return (
                    <div
                      className="activity-item"
                      key={activity.activity_id || `${leadId}-${activity.created_at}`}
                      onClick={() => handleViewLead(leadId)}
                    >
                      <div className="activity-icon">
                        <i className={`fas fa-${getActivityIcon(activity.type)}`}></i>
                      </div>
                      <div className="activity-content">
                        <div className="activity-header">
                          <span className="activity-type">{activity.type}</span>
                          <span className="activity-date">{formatDate(activity.created_at)}</span>
                        </div>
                        <div className="activity-description">
                          <span className="link-btn">{activity.company || 'N/A'}</span>
                          {' - '}
                          {activity.description || ''}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="empty-state">
                <i className="fas fa-clock"></i>
                <p>No recent activities</p>
              </div>
            )}
          </div>
        </div>

        {/* Upcoming Demos Card */}
        <div className="dashboard-card">
          <div className="dashboard-card-header">
            <i className="fas fa-calendar-check"></i>
            <span>Upcoming Demos</span>
            <span className="badge-count">{upcomingDemos.length}</span>
          </div>
          <div className="dashboard-card-body">
            {demosLoading ? (
              <SectionSkeleton rows={5} />
            ) : demosError ? (
              <SectionError message={demosError} onRetry={loadUpcomingDemos} />
            ) : upcomingDemos.length > 0 ? (
              <div className="demo-list">
                {upcomingDemos.slice(0, 8).map((lead) => {
                  const leadId = lead.lead_id;
                  const demoStatus = lead.demo_status || 'Scheduled';
                  const firstContact = Array.isArray(lead.contacts) ? lead.contacts[0] : null;
                  const demoName = [firstContact?.first_name, firstContact?.last_name].filter(Boolean).join(' ') || '';

                  // Parse UTC date to user timezone
                  const { displayDay, displayMonth, displayTime } = parseDemoDate(lead.demo_date);

                  return (
                    <div
                      className="demo-item"
                      key={leadId}
                      onClick={() => handleViewLead(leadId)}
                    >
                      {/* DATE BLOCK - User timezone */}
                      <div className="demo-date-block">
                        <span className="demo-day">{displayDay}</span>
                        <span className="demo-month">{displayMonth}</span>
                      </div>

                      <div className="demo-info">
                        <div className="demo-company">
                          <span className="link-btn">{lead.company || 'Unnamed Dealership'}</span>
                        </div>
                        <div className="demo-time">
                          <i className="fas fa-clock"></i>
                          {displayTime}
                          <span className="timezone">({userTimezone})</span>
                        </div>
                        {demoName && (
                          <div className="demo-contact">
                            <i className="fas fa-user"></i> {demoName}
                          </div>
                        )}
                      </div>

                      <div className="demo-status-wrapper">
                        <span className="demo-status-badge">{demoStatus}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="empty-state">
                <i className="fas fa-calendar"></i>
                <p>No upcoming demos</p>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Timezone Footer */}
      <div className="dashboard-timezone-footer">
        <i className="fas fa-clock"></i>
        All times displayed in your timezone: <strong>{userTimezone}</strong>
        {user?.timezone ? (
          <span className="timezone-source">(from user profile)</span>
        ) : (
          <span className="timezone-source">(using app default)</span>
        )}
      </div>

      {/* ViewLead Overlay */}
      {viewLeadId && (
        <ViewLead
          leadId={viewLeadId}
          embedded={true}
          onClose={handleCloseView}
          onChanged={refreshDashboard}
        />
      )}
    </Layout>
  );
}

export default Dashboard;